// Multi-page coordinate system extensions

import { CoordinateSpace, Coordinates, ContentRect } from './CoordinateSpaces';
import { SpaceTransforms } from './SpaceTransforms';

export interface PageLayout {
  pageIndex: number;
  pageWidth: number;
  pageHeight: number;
  contentRect: ContentRect;
  offsetY: number; // Cumulative Y offset in viewport
}

export interface MultiPageContext {
  pages: PageLayout[];
  pageGap: number; // Gap between pages in viewport
  viewportWidth: number;
  viewportHeight: number;
  scrollOffset: { x: number; y: number };
}

export class MultiPageCoordinates extends SpaceTransforms {
  /**
   * Transform coordinates with multi-page awareness
   */
  static transformMultiPage(
    coords: Coordinates,
    targetSpace: CoordinateSpace,
    context: {
      pageIndex: number;
      multiPageContext: MultiPageContext;
      contentRect?: ContentRect;
    }
  ): Coordinates {
    const { pageIndex, multiPageContext } = context;
    const page = multiPageContext.pages[pageIndex];

    if (!page) {
      throw new Error(`Page ${pageIndex} not found in multi-page context`);
    }

    // Handle multi-page specific transforms
    if (coords.space === CoordinateSpace.PAGE && targetSpace === CoordinateSpace.VIEWPORT) {
      return {
        x: coords.x,
        y: coords.y + page.offsetY,
        space: CoordinateSpace.VIEWPORT
      };
    }

    if (coords.space === CoordinateSpace.VIEWPORT && targetSpace === CoordinateSpace.PAGE) {
      return {
        x: coords.x,
        y: coords.y - page.offsetY,
        space: CoordinateSpace.PAGE
      };
    }

    // Fall back to base transform for other space combinations
    return super.transform(coords, targetSpace, {
      contentRect: context.contentRect || page.contentRect,
      pageIndex,
      pageHeight: page.pageHeight,
      pageGap: multiPageContext.pageGap
    });
  }

  /**
   * Calculate page layouts for multi-page document
   */
  static calculatePageLayouts(
    pages: Array<{
      width: number;
      height: number;
      contentRect: ContentRect;
    }>,
    pageGap: number = 20
  ): PageLayout[] {
    let cumulativeOffsetY = 0;
    
    return pages.map((page, index) => {
      const layout: PageLayout = {
        pageIndex: index,
        pageWidth: page.width,
        pageHeight: page.height,
        contentRect: page.contentRect,
        offsetY: cumulativeOffsetY
      };

      // Add page height and gap for next page
      cumulativeOffsetY += page.height + pageGap;

      return layout;
    });
  }

  /**
   * Find which page contains a given viewport Y coordinate
   */
  static findPageAtViewportY(
    viewportY: number,
    multiPageContext: MultiPageContext
  ): { pageIndex: number; pageY: number } | null {
    for (let i = 0; i < multiPageContext.pages.length; i++) {
      const page = multiPageContext.pages[i];
      const pageEndY = page.offsetY + page.pageHeight;

      if (viewportY >= page.offsetY && viewportY <= pageEndY) {
        return {
          pageIndex: i,
          pageY: viewportY - page.offsetY
        };
      }
    }

    return null;
  }

  /**
   * Calculate which pages are visible in the current viewport
   */
  static getVisiblePages(
    multiPageContext: MultiPageContext
  ): number[] {
    const { scrollOffset, viewportHeight, pages } = multiPageContext;
    const viewportTop = scrollOffset.y;
    const viewportBottom = viewportTop + viewportHeight;
    
    const visiblePages: number[] = [];

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      const pageTop = page.offsetY;
      const pageBottom = page.offsetY + page.pageHeight;

      // Check if page intersects with viewport (with padding for preloading)
      const padding = 100; // Preload pages within 100px of viewport
      if (pageBottom >= viewportTop - padding && pageTop <= viewportBottom + padding) {
        visiblePages.push(i);
      }
    }

    return visiblePages;
  }

  /**
   * Calculate total document height for scrolling
   */
  static calculateTotalDocumentHeight(multiPageContext: MultiPageContext): number {
    if (multiPageContext.pages.length === 0) return 0;
    
    const lastPage = multiPageContext.pages[multiPageContext.pages.length - 1];
    return lastPage.offsetY + lastPage.pageHeight;
  }

  /**
   * Transform batch coordinates for multi-page rendering
   */
  static transformBatchMultiPage(
    coordsList: Array<{
      coords: Coordinates;
      pageIndex: number;
    }>,
    targetSpace: CoordinateSpace,
    multiPageContext: MultiPageContext
  ): Coordinates[] {
    return coordsList.map(({ coords, pageIndex }) => 
      this.transformMultiPage(coords, targetSpace, {
        pageIndex,
        multiPageContext,
        contentRect: multiPageContext.pages[pageIndex]?.contentRect
      })
    );
  }

  /**
   * Create multi-page context from backend layout plan
   */
  static createMultiPageContext(
    backendPages: Array<{
      width: number;
      height: number;
      contentRect: ContentRect;
    }>,
    viewportDimensions: { width: number; height: number },
    scrollOffset: { x: number; y: number } = { x: 0, y: 0 },
    pageGap: number = 20
  ): MultiPageContext {
    const pageLayouts = this.calculatePageLayouts(backendPages, pageGap);

    return {
      pages: pageLayouts,
      pageGap,
      viewportWidth: viewportDimensions.width,
      viewportHeight: viewportDimensions.height,
      scrollOffset
    };
  }
}
